export const MARKETPLACE_CACHE_SCHEMA_VERSION = 2;
const MAX_EVENTS_PER_TOKEN = 600;

export const INITIAL_MARKETPLACE_STATE = {
  tokensById: {},
  tokenOrder: [],
  metadataByUri: {},
  eventsByTokenId: {},
  bidHistoryByKey: {},
  blockTimestampByNumber: {},
  lastScannedBlocks: {
    mint: null,
  },
  eventCursor: {
    lastProcessedEventBlock: null,
  },
  dirtyTokenIds: [],
  loadingStatus: {
    bootstrapping: false,
    visibleHydration: false,
    backgroundHydration: false,
    staleFromCache: false,
    fromCache: false,
  },
  errors: {
    global: null,
    byToken: {},
  },
};

function normalizeTokenId(tokenId) {
  return tokenId == null ? "" : String(tokenId);
}

function mergeToken(existing, patch) {
  return {
    ...(existing || {}),
    ...patch,
    auctionData: {
      ...((existing && existing.auctionData) || {}),
      ...(patch && patch.auctionData ? patch.auctionData : {}),
    },
    updatedAt: Date.now(),
  };
}

function uniqueTokenOrder(order) {
  const seen = new Set();
  const next = [];
  for (const id of order) {
    const normalized = normalizeTokenId(id);
    if (!normalized || seen.has(normalized)) continue;
    seen.add(normalized);
    next.push(normalized);
  }
  return next;
}

function sortTokenOrder(order) {
  return [...order].sort((a, b) => Number(a) - Number(b));
}

export function marketplaceReducer(state, action) {
  switch (action.type) {
    case "RESET":
      return {
        ...INITIAL_MARKETPLACE_STATE,
      };

    case "HYDRATE_FROM_CACHE": {
      const payload = action.payload || {};
      return {
        ...state,
        ...payload,
        tokensById: payload.tokensById || state.tokensById,
        tokenOrder: payload.tokenOrder || state.tokenOrder,
        metadataByUri: payload.metadataByUri || state.metadataByUri,
        eventsByTokenId: payload.eventsByTokenId || state.eventsByTokenId,
        bidHistoryByKey: payload.bidHistoryByKey || state.bidHistoryByKey,
        blockTimestampByNumber: payload.blockTimestampByNumber || state.blockTimestampByNumber,
        lastScannedBlocks: payload.lastScannedBlocks || state.lastScannedBlocks,
        eventCursor: payload.eventCursor || state.eventCursor,
        loadingStatus: {
          ...state.loadingStatus,
          ...payload.loadingStatus,
          fromCache: true,
          staleFromCache: true,
        },
      };
    }

    case "ADD_TOKEN_IDS": {
      const tokenIds = (action.payload || []).map(normalizeTokenId).filter(Boolean);
      const mergedOrder = sortTokenOrder(uniqueTokenOrder([...state.tokenOrder, ...tokenIds]));

      if (mergedOrder.length === state.tokenOrder.length) {
        return state;
      }

      return {
        ...state,
        tokenOrder: mergedOrder,
      };
    }

    case "UPSERT_TOKEN": {
      const token = action.payload || {};
      const tokenId = normalizeTokenId(token.tokenId);
      if (!tokenId) return state;

      const nextTokensById = {
        ...state.tokensById,
        [tokenId]: mergeToken(state.tokensById[tokenId], { ...token, tokenId }),
      };

      const nextOrder = state.tokenOrder.includes(tokenId)
        ? state.tokenOrder
        : sortTokenOrder(uniqueTokenOrder([...state.tokenOrder, tokenId]));

      return {
        ...state,
        tokensById: nextTokensById,
        tokenOrder: nextOrder,
      };
    }

    case "UPSERT_TOKENS": {
      const tokens = action.payload || [];
      if (!Array.isArray(tokens) || tokens.length === 0) return state;

      const nextTokensById = { ...state.tokensById };
      const nextIds = [];

      for (const token of tokens) {
        const tokenId = normalizeTokenId(token && token.tokenId);
        if (!tokenId) continue;
        nextIds.push(tokenId);
        nextTokensById[tokenId] = mergeToken(nextTokensById[tokenId], { ...token, tokenId });
      }

      const nextOrder = sortTokenOrder(uniqueTokenOrder([...state.tokenOrder, ...nextIds]));

      return {
        ...state,
        tokensById: nextTokensById,
        tokenOrder: nextOrder,
      };
    }

    case "MERGE_TOKEN": {
      const { tokenId, patch } = action.payload || {};
      const normalized = normalizeTokenId(tokenId);
      if (!normalized) return state;

      const nextTokensById = {
        ...state.tokensById,
        [normalized]: mergeToken(state.tokensById[normalized], patch || {}),
      };

      const nextOrder = state.tokenOrder.includes(normalized)
        ? state.tokenOrder
        : sortTokenOrder(uniqueTokenOrder([...state.tokenOrder, normalized]));

      return {
        ...state,
        tokensById: nextTokensById,
        tokenOrder: nextOrder,
      };
    }

    case "SET_METADATA": {
      const { uri, metadata } = action.payload || {};
      if (!uri) return state;
      return {
        ...state,
        metadataByUri: {
          ...state.metadataByUri,
          [uri]: metadata,
        },
      };
    }

    case "INDEX_EVENT": {
      const { tokenId, event } = action.payload || {};
      const normalized = normalizeTokenId(tokenId);
      if (!normalized || !event) return state;

      const existing = state.eventsByTokenId[normalized] || [];
      const exists = existing.some((item) => item.eventKey === event.eventKey);
      if (exists) return state;

      const next = [...existing, event].sort((a, b) => {
        const blockDelta = Number(a.blockNumber || 0) - Number(b.blockNumber || 0);
        if (blockDelta !== 0) return blockDelta;
        return Number(a.logIndex || 0) - Number(b.logIndex || 0);
      }).slice(-MAX_EVENTS_PER_TOKEN);

      return {
        ...state,
        eventsByTokenId: {
          ...state.eventsByTokenId,
          [normalized]: next,
        },
      };
    }

    case "INDEX_EVENTS": {
      const payload = action.payload || [];
      if (!Array.isArray(payload) || payload.length === 0) return state;

      const nextEventsByTokenId = { ...state.eventsByTokenId };

      for (const item of payload) {
        const tokenId = normalizeTokenId(item?.tokenId);
        const event = item?.event;
        if (!tokenId || !event) continue;

        const existing = nextEventsByTokenId[tokenId] || [];
        if (existing.some((entry) => entry.eventKey === event.eventKey)) {
          continue;
        }

        nextEventsByTokenId[tokenId] = [...existing, event].sort((a, b) => {
          const blockDelta = Number(a.blockNumber || 0) - Number(b.blockNumber || 0);
          if (blockDelta !== 0) return blockDelta;
          return Number(a.logIndex || 0) - Number(b.logIndex || 0);
        }).slice(-MAX_EVENTS_PER_TOKEN);
      }

      return {
        ...state,
        eventsByTokenId: nextEventsByTokenId,
      };
    }

    case "SET_BID_HISTORY": {
      const { key, history } = action.payload || {};
      if (!key) return state;
      return {
        ...state,
        bidHistoryByKey: {
          ...state.bidHistoryByKey,
          [key]: history || [],
        },
      };
    }

    case "SET_BLOCK_TIMESTAMP": {
      const { blockNumber, timestamp } = action.payload || {};
      if (blockNumber == null || timestamp == null) return state;
      return {
        ...state,
        blockTimestampByNumber: {
          ...state.blockTimestampByNumber,
          [String(blockNumber)]: timestamp,
        },
      };
    }

    case "MARK_DIRTY": {
      const tokenIds = (action.payload || []).map(normalizeTokenId).filter(Boolean);
      if (tokenIds.length === 0) return state;
      return {
        ...state,
        dirtyTokenIds: uniqueTokenOrder([...state.dirtyTokenIds, ...tokenIds]),
      };
    }

    case "CLEAR_DIRTY": {
      const tokenIds = new Set((action.payload || []).map(normalizeTokenId).filter(Boolean));
      if (tokenIds.size === 0) {
        return {
          ...state,
          dirtyTokenIds: [],
        };
      }
      return {
        ...state,
        dirtyTokenIds: state.dirtyTokenIds.filter((id) => !tokenIds.has(id)),
      };
    }

    case "SET_LOADING":
      return {
        ...state,
        loadingStatus: {
          ...state.loadingStatus,
          ...(action.payload || {}),
        },
      };

    case "SET_TOKEN_ERROR": {
      const { tokenId, error } = action.payload || {};
      if (!tokenId) return state;
      return {
        ...state,
        errors: {
          ...state.errors,
          byToken: {
            ...state.errors.byToken,
            [String(tokenId)]: error,
          },
        },
      };
    }

    case "SET_GLOBAL_ERROR":
      return {
        ...state,
        errors: {
          ...state.errors,
          global: action.payload || null,
        },
      };

    case "SET_LAST_SCANNED_BLOCK": {
      const { mint } = action.payload || {};
      return {
        ...state,
        lastScannedBlocks: {
          ...state.lastScannedBlocks,
          ...(mint == null ? {} : { mint }),
        },
      };
    }

    case "SET_EVENT_CURSOR": {
      const { lastProcessedEventBlock } = action.payload || {};
      return {
        ...state,
        eventCursor: {
          ...state.eventCursor,
          ...(lastProcessedEventBlock == null ? {} : { lastProcessedEventBlock }),
        },
      };
    }

    default:
      return state;
  }
}
